import { Component, computed, effect, NgZone, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { DataService } from '@app/common-codes/services/DataService';
import { DependencyInjectionProvider } from '@app/common-codes/services/DependecyInjection.provider';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { PhotoCaptureComponent } from '../photo/photo-capture.component';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { Feedback } from '@app/common-codes/models/matwins-tenant/Feedback.model';
import { BubbleSmileyComponent } from '@app/common-codes/components/nested-components/bubble-smiley/bubble-smiley.component';
import { LoaderService } from '@app/common-codes/services/loader.service';
import { FaceRecognitionService } from '../../../services/face-recognition.service';
import { Face } from '@app/common-codes/models/matwins-tenant/Face.model';

@Component({
    selector: 'app-checkout',
    templateUrl: './checkout.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, PhotoCaptureComponent, MultiSelectComponent, BubbleSmileyComponent]
})
export class CheckoutComponent {
    activeVisits = signal<Visit[]>([]);
    selectedVisitorId = signal<string[]>([]);
    selectedVisit = signal<Visit | undefined>(undefined);
    feedbackScore = signal<number | undefined>(undefined);
    feedbackComment = signal('');
    searchText = signal('');


    /** Fenêtre de recherche des visites actives : les dernières 24h seulement. */
    private readonly ACTIVE_VISITS_WINDOW_HOURS = 24;

    pos = computed(() => this.tenantService.currentPointOfService());
    dataService!: DataService<Visit>;

    private faceRecognitionProcessing = false;
    private facePhotoVersion = 0;
    private pendingFacePhoto?: string;

    showFeedbackForm = computed(() => {
        const pos = this.pos();
        return !!pos?.dS_Checkout_Feedbacks && !!this.selectedVisit();
    });

    filteredVisits = computed(() => {
        const text = this.searchText().toLowerCase();
        if (text.length < 3) return [];
        return this.activeVisits().filter(v =>
            v.visitor?.fullname?.toLowerCase().includes(text)
        );
    });

    profileNotFound = computed(() => {
        return this.searchText().length >= 3
            && this.filteredVisits().length === 0
            && this.selectedVisitorId().length === 0;
    });

    profileFound = computed(() => {
        return !!this.selectedVisit() && !this.showFeedbackForm();
    });

    activeVisitorsLookup = computed(() => {
        // Toutes les visites actives sont passées au multi-select qui filtre localement
        return this.activeVisits().map(v => ({
            id: v.visitor?.id?.toString() ?? '',
            key: v.visitor?.id?.toString() ?? '',
            value: v.visitor?.fullname ?? '',
            sortOrder: '',
            visible: true,
            additionalData: { visitId: v.id, startDate: v.startDate },
            children: undefined,
            selectedValue: undefined
        })).filter(item => !!item.id);
    });

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private translationService: TranslationService,
        private validationService: GenericValidationService,
        private ngZone: NgZone,
        private navService: NavigationService,
        private tenantService: CurrentTenantService,
        private alertModalService: AlertModalService,
        private loaderService: LoaderService,
        private faceRecognitionService: FaceRecognitionService,
        private di: DependencyInjectionProvider
    ) {
        this.dataService = this.di.getOrCreateDataService(Visit);

        effect(() => {
            const pos = this.pos();
            if (pos) {
                this.loadAllActiveVisits();
            }
        });
    }

    async loadAllActiveVisits(): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        if (!pos) return;

        this.loaderService.show();
        try {
            const visits = await this.dataService.execute<Visit[]>('GET', 'getActiveVisits', {
                fkPos: pos.id,
                sinceHours: this.ACTIVE_VISITS_WINDOW_HOURS
            });
            this.activeVisits.set(visits ?? []);
        } catch (err) {
            console.error('Erreur chargement visites actives:', err);
            this.alertModalService.showToast('Erreur chargement visites', ToastType.Error, 3000);
        } finally {
            this.loaderService.hide();
        }
    }

    onSearchTextChange(text: string): void {
        this.searchText.set(text);
        this.selectedVisitorId.set([]);
        this.selectedVisit.set(undefined);
        this.feedbackScore.set(undefined);
        this.feedbackComment.set('');
    }

    async onVisitorSelectChange(ids: string[]): Promise<void> {
        this.selectedVisitorId.set(ids);
        const visitorId = ids[0];
        if (!visitorId) {
            this.selectedVisit.set(undefined);
            return;
        }

        const visit = this.activeVisits().find(v => v.visitor?.id?.toString() === visitorId);
        if (!visit) {
            this.selectedVisit.set(undefined);
            return;
        }

        this.selectedVisit.set(visit);

        const pos = this.pos();
        if (!pos?.dS_Checkout_Feedbacks) {
            await this.autoCheckout(visit);
        }
    }

    async autoCheckout(visit: Visit): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        if (!pos) return;
        this.loaderService.show();

        try {
            await this.dataService.execute<Visit>('POST', 'checkoutVisit', {
                visitId: visit.id,
                posId: pos.id
            });
            this.alertModalService.showToast('Merci et bonne journée', ToastType.Success, 3000);
            setTimeout(() => this.goHome(), 1500);
        } catch (err) {
            console.error('Erreur checkout:', err);
            this.alertModalService.showToast('Erreur lors de la sortie', ToastType.Error, 3000);
        } finally {
            this.loaderService.hide();
        }
    }

    setFeedbackScore(score: number): void {
        this.feedbackScore.set(score);
    }

    async submitCheckout(): Promise<void> {
        this.loaderService.show();

        const pos = this.tenantService.currentPointOfService();
        const visit = this.selectedVisit();
        if (!pos || !visit) {
            this.loaderService.hide();
            return;
        }

        const score = this.feedbackScore();
        const comment = this.feedbackComment().trim();

        try {
            await this.dataService.execute<Visit>('POST', 'checkoutVisit', {
                visitId: visit.id,
                posId: pos.id,
                feedback: score !== undefined || comment.length > 0
                    ? { score: score ?? 0, comment: comment || undefined }
                    : undefined
            });
            this.alertModalService.showToast('Merci et bonne journée', ToastType.Success, 3000);
            this.goHome();
        } catch (err) {
            console.error('Erreur checkout:', err);
            this.alertModalService.showToast('Erreur lors de la sortie', ToastType.Error, 3000);
        } finally {
            this.loaderService.hide();
        }
    }

    reset(): void {
        this.activeVisits.set([]);
        this.selectedVisitorId.set([]);
        this.selectedVisit.set(undefined);
        this.feedbackScore.set(undefined);
        this.feedbackComment.set('');
        this.searchText.set('');
    }

    onPhotoFile($event: File) { /* La photo ne sert qu'a la reconnaissance faciale au checkout. */ }

    onPhotoCaptured(base64: string): void {
        if (!this.pos()?.dS_FaceRecognition) return;
        // Une nouvelle photo (retake) remplace la precedente : la derniere
        // capture doit toujours etre celle analysee.
        this.facePhotoVersion++;
        this.pendingFacePhoto = base64;
        void this.runFaceRecognition();
    }

    /**
     * Extrait l'embedding facial de la derniere photo capturee et retrouve le
     * visiteur correspondant parmi les visites actives du POS (match serveur
     * via api/faces/match). Ne s'applique que si aucun visiteur n'est deja
     * selectionne.
     */
    private async runFaceRecognition(): Promise<void> {
        if (this.faceRecognitionProcessing) return;
        this.faceRecognitionProcessing = true;

        try {
            while (this.pendingFacePhoto) {
                const photo = this.pendingFacePhoto;
                const version = this.facePhotoVersion;
                this.pendingFacePhoto = undefined;

                const embedding = await this.faceRecognitionService.extractEmbedding(photo);
                if (!embedding || version !== this.facePhotoVersion) continue;

                const posId = this.pos()?.id ?? 0;
                const faceDataService = this.di.getOrCreateDataService(Face);
                const match = await faceDataService.execute<{
                    matched: boolean;
                    faceId?: number;
                    visitorId?: number;
                }>('POST', 'match', { posId, embedding });
                console.log('[face-reco] reponse api/faces/match (checkout):', JSON.stringify(match));

                if (version !== this.facePhotoVersion) continue;
                if (!match?.matched || !match.visitorId) continue;

                const alreadyIdentified = !!this.selectedVisit() || this.selectedVisitorId().length > 0;
                const hasActiveVisit = this.activeVisits().some(v => v.visitor?.id === match.visitorId);
                if (hasActiveVisit && !alreadyIdentified) {
                    await this.onVisitorSelectChange([String(match.visitorId)]);
                    this.alertModalService.showToast(
                        this.translationService.instant('vues.ipad.face.recognized'),
                        ToastType.Success, 3000
                    );
                }
            }
        } catch (err) {
            console.error('Face recognition error:', err);
        } finally {
            this.faceRecognitionProcessing = false;
        }
    }

    goBack(): void { this.navService.goBack(); }

    goHome(): void { this.navService.navigateTo('/ipad/home'); }
}